import React, { Fragment, useEffect, useRef, useState, ReactNode, useCallback } from 'react';
import { GameComponentArgs, GameEnum } from "../../App";
import { Button } from '../shared/button/button';
import './ski.css';
import Skier from './game-engine/skier';
import Tree from './game-engine/tree';
import { GlobalScoreComponent, RaceResult } from './components/global-score';
import { AnimationContext, getAnimationFunction, resetTreeSpawner, initializeScene } from './game-engine/global-animation';
import { RACE_DISTANCE } from './game-engine/constants';

export enum SkiGameStateEnum {
    notStarted = "notStarted",
    countdown = "countdown",
    racing = "racing",
    raceFinished = "raceFinished",
    allRacesComplete = "allRacesComplete",
}

export class GameState {
    _gameState: SkiGameStateEnum = SkiGameStateEnum.notStarted;
    skier: Skier = new Skier(this);
    trees: Tree[] = [];

    // Race tracking
    currentRace: number = 1;
    raceTimes: number[] = [];
    raceCompleted: boolean[] = [];
    raceTreeHits: number[] = [];
    treeHits: number = 0;
    distanceTraveled: number = 0;
    raceStartTime: number = 0;

    onSetGamePhase?: (phase: SkiGameStateEnum) => void;

    get gameState() {
        return this._gameState;
    }

    set gameState(newValue: SkiGameStateEnum) {
        this._gameState = newValue;
        this.onSetGamePhase?.(newValue);
    }

    resetForNewRace() {
        this.raceTreeHits.push(this.treeHits);
        this.trees = [];
        this.treeHits = 0;
        this.distanceTraveled = 0;
        this.skier.reset();
        resetTreeSpawner();
    }
}

export default function Ski({ setCurrentGame }: GameComponentArgs) {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const gameStateRef = useRef<GameState>(new GameState());
    const animationContextRef = useRef<AnimationContext | null>(null);

    const [gamePhase, setGamePhase] = useState<SkiGameStateEnum>(SkiGameStateEnum.notStarted);
    const [countdown, setCountdown] = useState<number>(3);
    const [currentSpeed, setCurrentSpeed] = useState<number>(0);
    const [currentDistance, setCurrentDistance] = useState<number>(0);
    const [raceResults, setRaceResults] = useState<RaceResult[]>([]);
    const [lastRaceCompleted, setLastRaceCompleted] = useState<boolean>(false);
    const [lastRaceTime, setLastRaceTime] = useState<number>(0);

    const getGameState = () => gameStateRef.current;
    getGameState().onSetGamePhase = setGamePhase;

    const startCountdown = useCallback(() => {
        const gameState = getGameState();
        gameState.gameState = SkiGameStateEnum.countdown;
        setCountdown(3);

        let count = 3;
        const countdownInterval = setInterval(() => {
            count--;
            setCountdown(count);
            if (count === 0) {
                clearInterval(countdownInterval);
                gameState.gameState = SkiGameStateEnum.racing;
                gameState.raceStartTime = Date.now();
            }
        }, 1000);
    }, []);

    const onRaceEnd = useCallback((completed: boolean) => {
        const gameState = getGameState();
        setLastRaceCompleted(completed);
        setLastRaceTime(gameState.raceTimes[gameState.raceTimes.length - 1]);
    }, []);

    const startAnimation = useCallback(() => {
        const gameState = getGameState();
        animationContextRef.current = {
            ...animationContextRef.current as AnimationContext,
            lastTime: Date.now(),
            gameState,
            stop: false,
            onRaceEnd
        };
        const animate = getAnimationFunction(animationContextRef.current as AnimationContext);
        animate();

        // Update HUD periodically
        const hudInterval = setInterval(() => {
            if (animationContextRef.current?.stop) {
                clearInterval(hudInterval);
                return;
            }
            setCurrentSpeed(Math.round(gameState.skier.speed));
            setCurrentDistance(Math.round(gameState.distanceTraveled));
        }, 100);
    }, [onRaceEnd]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width;
        canvas.height = rect.height;

        const zoom = canvas.height / 430;

        ctx.imageSmoothingEnabled = false;

        animationContextRef.current = {
            lastTime: Date.now(),
            ctx,
            gameState: getGameState(),
            stop: false,
            zoom,
            onRaceEnd
        };

        // Reset spawner state and pre-populate scene with trees and track lines
        resetTreeSpawner();
        initializeScene(getGameState());

        startAnimation();

        return () => {
            if (animationContextRef.current) {
                animationContextRef.current.stop = true;
            }
        };
    }, [startAnimation, onRaceEnd]);

    // Keyboard controls
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const gameState = getGameState();
            if (e.key === 'ArrowLeft') {
                gameState.skier.inputDirection = -1;
            } else if (e.key === 'ArrowRight') {
                gameState.skier.inputDirection = 1;
            } else if (e.key === 'ArrowUp') {
                gameState.skier.isTucking = true;
            }
        };

        const handleKeyUp = (e: KeyboardEvent) => {
            const gameState = getGameState();
            if ((e.key === 'ArrowLeft' && gameState.skier.inputDirection === -1) ||
                (e.key === 'ArrowRight' && gameState.skier.inputDirection === 1)) {
                gameState.skier.inputDirection = 0;
            } else if (e.key === 'ArrowUp') {
                gameState.skier.isTucking = false;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, []);

    // Touch controls for mobile
    useEffect(() => {
        const handleTouchStart = (e: TouchEvent) => {
            const target = e.target as HTMLElement;
            // Allow clicks on buttons and other interactive elements
            if (target.closest('button') || target.closest('a')) {
                return;
            }

            e.preventDefault();
            const gameState = getGameState();
            const touch = e.touches[0];
            const screenWidth = window.innerWidth;
            const touchX = touch.clientX;

            // Left third: turn left, Right third: turn right, Middle third: tuck
            if (touchX < screenWidth / 3) {
                gameState.skier.inputDirection = -1;
            } else if (touchX > screenWidth * 2 / 3) {
                gameState.skier.inputDirection = 1;
            } else {
                gameState.skier.isTucking = true;
            }
        };

        const handleTouchEnd = (e: TouchEvent) => {
            const gameState = getGameState();
            // Reset all controls when touch ends
            if (e.touches.length === 0) {
                gameState.skier.inputDirection = 0;
                gameState.skier.isTucking = false;
            }
        };

        const handleContextMenu = (e: Event) => {
            e.preventDefault();
        };

        window.addEventListener('touchstart', handleTouchStart, { passive: false });
        window.addEventListener('touchend', handleTouchEnd);
        window.addEventListener('contextmenu', handleContextMenu);

        return () => {
            window.removeEventListener('touchstart', handleTouchStart);
            window.removeEventListener('touchend', handleTouchEnd);
            window.removeEventListener('contextmenu', handleContextMenu);
        };
    }, []);

    const goToHome = () => {
        setCurrentGame(GameEnum.Home);
    };

    const startRace = () => {
        startCountdown();
    };

    const nextRace = () => {
        const gameState = getGameState();

        // Single race - go directly to results
        const results: RaceResult[] = gameState.raceTimes.map((time, i) => ({
            time,
            completed: gameState.raceCompleted[i],
            treeHits: gameState.raceTreeHits[i]
        }));
        setRaceResults(results);
        gameState.gameState = SkiGameStateEnum.allRacesComplete;
    };

    const retry = () => {
        gameStateRef.current = new GameState();
        const gameState = gameStateRef.current;
        gameState.onSetGamePhase = setGamePhase;
        setRaceResults([]);
        setCurrentSpeed(0);
        setCurrentDistance(0);

        // Reset and initialize scene
        resetTreeSpawner();
        initializeScene(gameState);

        gameState.gameState = SkiGameStateEnum.notStarted;
        startAnimation();
    };

    const formatTime = (ms: number): string => {
        const totalSeconds = Math.floor(ms / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        const centiseconds = Math.floor((ms % 1000) / 10);
        return `${minutes}:${seconds.toString().padStart(2, '0')}.${centiseconds.toString().padStart(2, '0')}`;
    };

    let centerElements: ReactNode = undefined;

    if (gamePhase === SkiGameStateEnum.notStarted) {
        centerElements = <Button onClick={startRace}>Commencer la descente !</Button>;
    }

    if (gamePhase === SkiGameStateEnum.countdown) {
        centerElements = <div className="countdown">{countdown}</div>;
    }

    if (gamePhase === SkiGameStateEnum.raceFinished) {
        centerElements = <div className="race-result">
            <div className="status">{lastRaceCompleted ? 'Arrivee !' : 'Trop de chutes !'}</div>
            <div className="time">{formatTime(lastRaceTime)}</div>
            <Button onClick={nextRace}>Voir les resultats</Button>
        </div>;
    }

    if (gamePhase === SkiGameStateEnum.allRacesComplete) {
        centerElements = <Fragment>
            <GlobalScoreComponent races={raceResults} />
            <Button className="retry-button" onClick={retry}>Refaire une descente !</Button>
        </Fragment>;
    }

    let hudElement: ReactNode = undefined;
    if (gamePhase === SkiGameStateEnum.racing || gamePhase === SkiGameStateEnum.countdown) {
        hudElement = <div className="hud">
            <div className="speed">{currentSpeed} m/s</div>
            <div className="distance">{currentDistance} / {RACE_DISTANCE} m</div>
        </div>;
    }

    return <Fragment>
        <div className="canvas-container ski">
            <canvas ref={canvasRef} />
        </div>
        <div className="interface ski">
            <div className='top'>
                <div className="title-container">
                    <div className='title'>Descends la piste le plus vite possible !</div>
                </div>
                <div className="top-bar">
                    <div className='consigne'>
                        Utilise les fleches pour eviter les sapins
                    </div>
                </div>
                {hudElement}
            </div>
            <div className='center'>
                {centerElements}
            </div>
            <div className='bottom'>
                <Button className="go-back-button" onClick={goToHome}>Retourner a Cervens</Button>
            </div>
        </div>
    </Fragment>;
}
